/**
 * Composant carte pour afficher les positions des livreurs en temps réel
 * Utilise Leaflet et WebSocket pour les mises à jour en temps réel
 */

import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, MapPin, Clock, Navigation } from 'lucide-react';
import { useMultipleLivreursWebSocket } from '../hooks/useLivreursWebSocket';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Créer une icône personnalisée pour les livreurs
const createLivreurIcon = (statut, vehicule) => {
  const getColor = () => {
    switch (statut) {
      case 'en_livraison':
      case 'en_route':
        return '#F59E0B'; // Orange
      case 'au_restaurant':
        return '#3B82F6'; // Bleu
      case 'arrive':
        return '#8B5CF6'; // Violet
      case 'pause':
        return '#F59E0B'; // Jaune
      default:
        return '#10B981'; // Vert (disponible)
    }
  };

  const getIconHtml = (color) => {
    return `
      <div style="
        background-color: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 16px;
        ">🚚</div>
      </div>
    `;
  };

  return L.divIcon({
    className: 'livreur-marker',
    html: getIconHtml(getColor()),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Composant pour ajuster la carte quand les positions changent
const MapBoundsUpdater = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (!positions || Object.keys(positions).length === 0) {
      return;
    }

    const validPositions = Object.values(positions).filter(
      pos => pos && typeof pos.latitude === 'number' && typeof pos.longitude === 'number'
    );

    if (validPositions.length === 0) {
      return;
    }

    if (validPositions.length === 1) {
      // Centrer sur un seul point
      map.setView([validPositions[0].latitude, validPositions[0].longitude], 13);
    } else {
      // Ajuster les bounds pour tous les points
      const bounds = L.latLngBounds(validPositions.map(pos => [pos.latitude, pos.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, positions]);

  return null;
};

const LivreursMap = ({ livreurs = [], livraisonSuiviGPS = true, height = '500px' }) => {
  const [selectedLivreur, setSelectedLivreur] = useState(null);

  // Filtrer les livreurs actifs avec positions
  const livreursAvecPositions = livreurs.filter(
    l => l.actif && l.positionLatitude && l.positionLongitude
  );

  const livreurIds = livreursAvecPositions.map(l => l.id);

  // Connexion WebSocket pour les positions en temps réel
  const { positions, statuts, connected, error } = useMultipleLivreursWebSocket(
    livreurIds,
    livraisonSuiviGPS && livreurIds.length > 0
  );

  // Calculer le centre de la carte
  const getMapCenter = () => {
    if (livreursAvecPositions.length === 0) {
      return [46.7111, 1.7191]; // Centre de la France par défaut
    }

    const validPositions = livreursAvecPositions
      .filter(l => l.positionLatitude && l.positionLongitude)
      .map(l => [l.positionLatitude, l.positionLongitude]);

    if (validPositions.length === 0) {
      return [46.7111, 1.7191];
    }

    // Moyenne des positions
    const sumLat = validPositions.reduce((sum, [lat]) => sum + lat, 0);
    const sumLng = validPositions.reduce((sum, [, lng]) => sum + lng, 0);
    return [sumLat / validPositions.length, sumLng / validPositions.length];
  };

  if (!livraisonSuiviGPS) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-yellow-600" />
          <p className="text-sm text-yellow-800">
            Le suivi GPS est désactivé pour ce commerçant. Activez-le dans les paramètres pour voir les positions des livreurs sur la carte.
          </p>
        </div>
      </div>
    );
  }

  if (livreursAvecPositions.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-gray-400" />
          <p className="text-sm text-gray-600">
            Aucun livreur avec position GPS disponible pour l'instant.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statut WebSocket */}
      {connected && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Connexion WebSocket active - Mises à jour en temps réel</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Carte */}
      <div className="bg-white border border-gray-300 rounded-lg overflow-hidden" style={{ height }}>
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Mettre à jour les bounds quand les positions changent */}
          <MapBoundsUpdater positions={positions} />

          {/* Marqueurs des livreurs */}
          {livreursAvecPositions.map((livreur) => {
            // Utiliser la position en temps réel si disponible, sinon la position stockée
            const position = positions[livreur.id] || {
              latitude: livreur.positionLatitude,
              longitude: livreur.positionLongitude,
              timestamp: livreur.dernierePosition?.toISOString(),
            };

            if (!position || !position.latitude || !position.longitude) {
              return null;
            }

            const statutActuel = statuts[livreur.id] || livreur.statut || 'disponible';
            const statutLabel = {
              disponible: 'Disponible',
              en_livraison: 'En livraison',
              au_restaurant: 'Au restaurant',
              en_route: 'En route',
              arrive: 'Arrivé',
              pause: 'Pause',
            }[statutActuel] || 'Disponible';

            return (
              <Marker
                key={livreur.id}
                position={[position.latitude, position.longitude]}
                icon={createLivreurIcon(statutActuel, livreur.vehicule)}
                eventHandlers={{
                  click: () => setSelectedLivreur(livreur),
                }}
              >
                <Popup>
                  <div className="space-y-2">
                    <div className="font-semibold text-gray-900">
                      {livreur.nom} {livreur.prenom}
                    </div>
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Truck className="w-4 h-4" />
                        {livreur.vehicule || 'Non défini'}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Navigation className="w-4 h-4" />
                        {statutLabel}
                      </div>
                      {position.timestamp && (
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="w-4 h-4" />
                          {new Date(position.timestamp).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export default LivreursMap;


